// `Scope` — `{ organizationId, venueId? }` (spec/08-api-design.md §3).
//
// Scope is explicit (§1.3): organisation and venue come from the path, never from the session's
// "active organisation". This shape is what a resolved request scope looks like once the middleware
// has read it out of the path, and what a permission check, an audit row and a realtime channel
// are all keyed by.
import { z } from "../zod.js";
import { OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
export const ScopeSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    /** Absent when the scope is the whole organisation rather than one of its venues. */
    venueId: VenueIdSchema.optional(),
})
    .openapi('Scope', { description: 'The organisation, and optionally the venue, a request applies to.' });
/** The path parameters that produce a `Scope`. Reused by every scoped route. */
export const OrganizationScopeParamsSchema = z
    .object({ organizationId: OrganizationIdSchema })
    .openapi('OrganizationScopeParams');
export const VenueScopeParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema })
    .openapi('VenueScopeParams');
/**
 * The headers that carry scope when the path does not (spec/08 §2.7, spec/07 §6.1).
 *
 * A path is the normal place — `/v1/organizations/{organizationId}/…`. A header is for the routes
 * that are not nested under one, and for the station and kiosk principals whose scope is fixed by
 * the device rather than by the URL. Either way it is **explicit**: better-auth's
 * `activeOrganizationId` is a UI default and is never the source of authority for a read or a
 * write. The server never falls back to it, and `apps/server/scripts/check-scoped-queries.mjs`
 * fails the build if that name appears in server code outside a comment.
 */
export const ORGANIZATION_SCOPE_HEADER = 'X-Clavian-Organization';
export const VENUE_SCOPE_HEADER = 'X-Clavian-Venue';
//# sourceMappingURL=scope.js.map